import {
  DCKSLAP_RESOURCE_ADDRESS,
  GBOF_RESOURCE_ADDRESS,
  RADIX_API_GATEWAY_URL,
  REDDICKS_DEX_ROUTER_COMPONENT,
  REDDICKS_RESSOURCE_ADDRESS,
} from "../../constants";
import { DEX_SITE_URL } from "../../config/siteContent";

const REDDICKS_DEX_PAIRS_URL = `${DEX_SITE_URL}/api/pairs`;
const REDDICKS_DEX_ANALYTICS_URL = `${DEX_SITE_URL}/api/analytics`;
const REDDICKS_DEX_PAIR_ANALYTICS_URL = `${DEX_SITE_URL}/api/analytics/pair`;
const STORAGE_SCOPE = RADIX_API_GATEWAY_URL.includes("stokenet")
  ? "stokenet"
  : "mainnet";
const REDDICKS_DEX_ANALYTICS_CACHE_VERSION = "v2";
const REDDICKS_DEX_ANALYTICS_CACHE_KEY = `reddicksDexAnalytics.${STORAGE_SCOPE}.${REDDICKS_DEX_ANALYTICS_CACHE_VERSION}`;
const REDDICKS_DEX_ANALYTICS_TTL_MS = 10 * 60 * 1000;
const isBrowser = typeof window !== "undefined";
const isMainnetDexSource =
  STORAGE_SCOPE === "mainnet" && Boolean(REDDICKS_DEX_ROUTER_COMPONENT);
const SEVEN_DAY_WINDOW_SECONDS = 7 * 24 * 60 * 60;

const EMPTY_ROUTER_TOTAL = {
  address: null,
  totalVolumeUSD: 0,
  totalFeesUSD: 0,
};

const buildEmptyTokenMarket = (label, symbol = label) => ({
  status: "unavailable",
  label,
  symbol,
  name: label,
  pairLabel: "Official market unavailable",
  pairAddress: null,
  pairCreatedAt: null,
  priceUnit: "REDDICKS",
  currentPrice: null,
  bidPrice: null,
  askPrice: null,
  tvlUsd: 0,
  latestVolumeUsd: null,
  latestFeesUsd: null,
  apy7d: null,
  updatedAt: null,
  latestSnapshotDate: null,
  shortage: null,
});

const buildEmptyReddicksFamily = () => ({
  status: "unavailable",
  label: "REDDICKS",
  pairCount: 0,
  aggregateTvlUsd: 0,
  latestCreatedAt: null,
  largestPairTvlUsd: 0,
});

export const EMPTY_REDDICKS_DEX_ANALYTICS = {
  overview: {
    isAvailable: false,
    officialRouter: { ...EMPTY_ROUTER_TOTAL },
    supportingRouters: [],
    kpis: {
      marketShare: {
        officialVolumeSharePct: null,
        officialFeesSharePct: null,
        effectiveFeeRatePct: null,
      },
      flow: {
        sevenDay: {
          avgTvlUsd: null,
          avgVolumeUsd: null,
          avgFeesUsd: null,
          volumeToTvlPct: null,
          feeRateBps: null,
          tvlRangePct: null,
          sampleCount: 0,
        },
      },
      deltas: {
        oneDay: {
          tvlPct: null,
          volumePct: null,
          feesPct: null,
        },
        sevenDay: {
          tvlPct: null,
          volumePct: null,
          feesPct: null,
        },
      },
    },
    latestSnapshot: {
      date: null,
      stateVersion: null,
      totalValueLockedUSD: 0,
      volumeUSD: 0,
      feesUSD: 0,
    },
    reddicksFamily: buildEmptyReddicksFamily(),
  },
  trendRows: [],
  tokenMarkets: {
    reddicks: buildEmptyReddicksFamily(),
    gbof: buildEmptyTokenMarket("GBOF"),
    dckslap: buildEmptyTokenMarket("DCKSLAP"),
  },
  topPairs: [],
  fetchedAt: null,
  lastUpdated: null,
  isFresh: false,
};

const getStoredValue = (key) => {
  if (!isBrowser) return null;
  return window.localStorage.getItem(key);
};

const setStoredValue = (key, value) => {
  if (!isBrowser) return;
  window.localStorage.setItem(key, value);
};

const parseJson = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
};

const toNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export const toFiniteNumberOrNull = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const getArrayPayload = (value) =>
  Array.isArray(value?.data) ? value.data : Array.isArray(value) ? value : [];

const getReddicksDexPairsCandidateUrls = () => {
  return [REDDICKS_DEX_PAIRS_URL];
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url} (${response.status})`);
  }

  return response.json();
};

const fetchJsonWithFallback = async (urls) => {
  const candidates = Array.from(
    new Set(
      (Array.isArray(urls) ? urls : []).filter(
        (url) => typeof url === "string" && url
      )
    )
  );
  let lastError = null;

  for (const candidate of candidates) {
    try {
      return await fetchJson(candidate);
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) {
    throw lastError;
  }

  throw new Error("No REDDICKS DEX pair endpoints configured");
};

const normalizePairMeta = (pair) => ({
  address: typeof pair?.address === "string" ? pair.address : null,
  dexAddress: typeof pair?.dexAddress === "string" ? pair.dexAddress : null,
  baseToken: typeof pair?.baseToken === "string" ? pair.baseToken : null,
  quoteToken: typeof pair?.quoteToken === "string" ? pair.quoteToken : null,
  basePool: typeof pair?.basePool === "string" ? pair.basePool : null,
  quotePool: typeof pair?.quotePool === "string" ? pair.quotePool : null,
  baseAPY: toFiniteNumberOrNull(pair?.baseAPY),
  quoteAPY: toFiniteNumberOrNull(pair?.quoteAPY),
  baseTVL: toNumber(pair?.baseTVL),
  quoteTVL: toNumber(pair?.quoteTVL),
  tvlUSD: toNumber(pair?.tvlUSD),
  volume: toNumber(pair?.volume),
  createdAt: typeof pair?.createdAt === "string" ? pair.createdAt : null,
});

const sortPairsByTvl = (pairs) =>
  [...pairs].sort((left, right) => {
    const tvlDiff = toNumber(right?.tvlUSD) - toNumber(left?.tvlUSD);
    if (tvlDiff !== 0) {
      return tvlDiff;
    }

    return Date.parse(right?.createdAt || 0) - Date.parse(left?.createdAt || 0);
  });

const getLatestCreatedAt = (pairs) =>
  pairs.reduce((latest, pair) => {
    const candidateTimestamp = Date.parse(pair?.createdAt || "");
    const latestTimestamp = Date.parse(latest || "");

    if (!Number.isFinite(candidateTimestamp)) {
      return latest;
    }

    if (!Number.isFinite(latestTimestamp) || candidateTimestamp > latestTimestamp) {
      return pair.createdAt;
    }

    return latest;
  }, null);

const selectLatestStatEntry = (stats) =>
  [...(Array.isArray(stats) ? stats : [])]
    .map((entry) => ({
      date: toNumber(entry?.date),
      stateVersion: toNumber(entry?.stateVersion),
      totalValueLockedUSD: toNumber(entry?.totalValueLockedUSD),
      volumeUSD: toNumber(entry?.volumeUSD),
      feesUSD: toNumber(entry?.feesUSD),
      lpBaseUSD: toFiniteNumberOrNull(entry?.lpBaseUSD),
      lpQuoteUSD: toFiniteNumberOrNull(entry?.lpQuoteUSD),
    }))
    .filter((entry) => entry.date > 0)
    .sort(
      (left, right) =>
        right.date - left.date || right.stateVersion - left.stateVersion,
    )[0] || null;

export const buildTrendRows = (dailies) =>
  [...(Array.isArray(dailies) ? dailies : [])]
    .map((entry) => ({
      date: toNumber(entry?.date),
      stateVersion: toNumber(entry?.stateVersion),
      totalValueLockedUSD: toNumber(entry?.totalValueLockedUSD),
      volumeUSD: toNumber(entry?.volumeUSD),
      feesUSD: toNumber(entry?.feesUSD),
    }))
    .filter((entry) => entry.date > 0)
    .sort(
      (left, right) =>
        left.date - right.date || left.stateVersion - right.stateVersion,
    );

export const selectOfficialRouterTotal = (totals, routerAddress) => {
  const selectedTotal = (Array.isArray(totals) ? totals : []).find(
    (entry) => entry?.address === routerAddress,
  );

  if (!selectedTotal) {
    return { ...EMPTY_ROUTER_TOTAL };
  }

  return {
    address: selectedTotal.address,
    totalVolumeUSD: toNumber(selectedTotal.totalVolumeUSD),
    totalFeesUSD: toNumber(selectedTotal.totalFeesUSD),
  };
};

const toPercentDelta = (latestValue, baselineValue) => {
  const latestNumeric = toFiniteNumberOrNull(latestValue);
  const baselineNumeric = toFiniteNumberOrNull(baselineValue);

  if (latestNumeric === null || baselineNumeric === null || baselineNumeric === 0) {
    return null;
  }

  return ((latestNumeric - baselineNumeric) / Math.abs(baselineNumeric)) * 100;
};

export const buildRouterShareKpis = (totals, routerAddress) => {
  const normalizedTotals = (Array.isArray(totals) ? totals : [])
    .filter((entry) => typeof entry?.address === "string" && entry.address)
    .map((entry) => ({
      address: entry.address,
      totalVolumeUSD: toNumber(entry.totalVolumeUSD),
      totalFeesUSD: toNumber(entry.totalFeesUSD),
    }));
  const aggregateVolumeUSD = normalizedTotals.reduce(
    (total, entry) => total + entry.totalVolumeUSD,
    0,
  );
  const aggregateFeesUSD = normalizedTotals.reduce(
    (total, entry) => total + entry.totalFeesUSD,
    0,
  );
  const officialTotal = normalizedTotals.find(
    (entry) => entry.address === routerAddress,
  ) || {
    totalVolumeUSD: 0,
    totalFeesUSD: 0,
  };

  return {
    officialVolumeSharePct:
      aggregateVolumeUSD > 0
        ? (officialTotal.totalVolumeUSD / aggregateVolumeUSD) * 100
        : null,
    officialFeesSharePct:
      aggregateFeesUSD > 0
        ? (officialTotal.totalFeesUSD / aggregateFeesUSD) * 100
        : null,
    effectiveFeeRatePct:
      officialTotal.totalVolumeUSD > 0
        ? (officialTotal.totalFeesUSD / officialTotal.totalVolumeUSD) * 100
        : null,
  };
};

export const buildTrendDeltaKpis = (trendRows) => {
  const rows = Array.isArray(trendRows) ? trendRows : [];
  const latestRow = rows[rows.length - 1] || null;
  const getBaselineRow = (minimumAgeSeconds) => {
    if (!latestRow || !Number.isFinite(latestRow.date)) {
      return null;
    }

    const cutoffDate = latestRow.date - minimumAgeSeconds;
    const eligibleRows = rows.filter(
      (row) => Number.isFinite(row?.date) && row.date <= cutoffDate,
    );

    return eligibleRows[eligibleRows.length - 1] || null;
  };
  const oneDayBaselineRow = getBaselineRow(24 * 60 * 60);
  const sevenDayBaselineRow = getBaselineRow(7 * 24 * 60 * 60);

  return {
    oneDay: {
      tvlPct: toPercentDelta(
        latestRow?.totalValueLockedUSD,
        oneDayBaselineRow?.totalValueLockedUSD,
      ),
      volumePct: toPercentDelta(
        latestRow?.volumeUSD,
        oneDayBaselineRow?.volumeUSD,
      ),
      feesPct: toPercentDelta(latestRow?.feesUSD, oneDayBaselineRow?.feesUSD),
    },
    sevenDay: {
      tvlPct: toPercentDelta(
        latestRow?.totalValueLockedUSD,
        sevenDayBaselineRow?.totalValueLockedUSD,
      ),
      volumePct: toPercentDelta(
        latestRow?.volumeUSD,
        sevenDayBaselineRow?.volumeUSD,
      ),
      feesPct: toPercentDelta(latestRow?.feesUSD, sevenDayBaselineRow?.feesUSD),
    },
  };
};

const selectTrendWindowRows = (trendRows, windowSeconds) => {
  const rows = Array.isArray(trendRows) ? trendRows : [];
  const latestRow = rows[rows.length - 1] || null;

  if (!latestRow || !Number.isFinite(latestRow?.date)) {
    return [];
  }

  const cutoffDate = latestRow.date - windowSeconds;

  return rows.filter(
    (row) =>
      Number.isFinite(row?.date) &&
      row.date <= latestRow.date &&
      row.date >= cutoffDate,
  );
};

export const buildTrendFlowKpis = (trendRows) => {
  const sevenDayRows = selectTrendWindowRows(
    trendRows,
    SEVEN_DAY_WINDOW_SECONDS,
  );

  if (sevenDayRows.length === 0) {
    return {
      sevenDay: {
        avgTvlUsd: null,
        avgVolumeUsd: null,
        avgFeesUsd: null,
        volumeToTvlPct: null,
        feeRateBps: null,
        tvlRangePct: null,
        sampleCount: 0,
      },
    };
  }

  const sampleCount = sevenDayRows.length;
  const totalTvlUsd = sevenDayRows.reduce(
    (total, row) => total + toNumber(row.totalValueLockedUSD),
    0,
  );
  const totalVolumeUsd = sevenDayRows.reduce(
    (total, row) => total + toNumber(row.volumeUSD),
    0,
  );
  const totalFeesUsd = sevenDayRows.reduce(
    (total, row) => total + toNumber(row.feesUSD),
    0,
  );
  const avgTvlUsd = totalTvlUsd / sampleCount;
  const avgVolumeUsd = totalVolumeUsd / sampleCount;
  const avgFeesUsd = totalFeesUsd / sampleCount;
  const tvlValues = sevenDayRows.map((row) => toNumber(row.totalValueLockedUSD));
  const maxTvlUsd = tvlValues.length > 0 ? Math.max(...tvlValues) : null;
  const minTvlUsd = tvlValues.length > 0 ? Math.min(...tvlValues) : null;

  return {
    sevenDay: {
      avgTvlUsd,
      avgVolumeUsd,
      avgFeesUsd,
      volumeToTvlPct: avgTvlUsd > 0 ? (avgVolumeUsd / avgTvlUsd) * 100 : null,
      feeRateBps: totalVolumeUsd > 0 ? (totalFeesUsd / totalVolumeUsd) * 10_000 : null,
      tvlRangePct:
        avgTvlUsd > 0 && maxTvlUsd !== null && minTvlUsd !== null
          ? ((maxTvlUsd - minTvlUsd) / avgTvlUsd) * 100
          : null,
      sampleCount,
    },
  };
};

const buildSupportingRouterTotals = (totals, routerAddress) =>
  (Array.isArray(totals) ? totals : [])
    .filter((entry) => entry?.address && entry.address !== routerAddress)
    .map((entry) => ({
      address: entry.address,
      totalVolumeUSD: toNumber(entry.totalVolumeUSD),
      totalFeesUSD: toNumber(entry.totalFeesUSD),
    }))
    .sort((left, right) => right.totalVolumeUSD - left.totalVolumeUSD);

export const selectPrimaryTokenPair = (pairs, tokenAddress, routerAddress) =>
  sortPairsByTvl(
    (Array.isArray(pairs) ? pairs : [])
      .map(normalizePairMeta)
      .filter(
        (pair) =>
          pair.address &&
          pair.dexAddress === routerAddress &&
          (pair.baseToken === tokenAddress || pair.quoteToken === tokenAddress),
      ),
  )[0] || null;

export const buildReddicksFamilySummary = (
  pairs,
  routerAddress,
  reddicksResourceAddress,
) => {
  const reddicksPairs = sortPairsByTvl(
    (Array.isArray(pairs) ? pairs : [])
      .map(normalizePairMeta)
      .filter(
        (pair) =>
          pair.address &&
          pair.dexAddress === routerAddress &&
          (pair.baseToken === reddicksResourceAddress ||
            pair.quoteToken === reddicksResourceAddress),
      ),
  );

  if (reddicksPairs.length === 0) {
    return buildEmptyReddicksFamily();
  }

  return {
    status: "live",
    label: "REDDICKS",
    pairCount: reddicksPairs.length,
    aggregateTvlUsd: reddicksPairs.reduce(
      (total, pair) => total + toNumber(pair.tvlUSD),
      0,
    ),
    latestCreatedAt: getLatestCreatedAt(reddicksPairs),
    largestPairTvlUsd: toNumber(reddicksPairs[0]?.tvlUSD),
  };
};

const buildPairSpreadPct = (pairSnapshot) => {
  const askPrice = toFiniteNumberOrNull(pairSnapshot?.askPrice);
  const bidPrice = toFiniteNumberOrNull(pairSnapshot?.bidPrice);
  const lastPrice = toFiniteNumberOrNull(pairSnapshot?.lastPrice);

  if (
    askPrice === null ||
    bidPrice === null ||
    lastPrice === null ||
    lastPrice === 0
  ) {
    return null;
  }

  return ((askPrice - bidPrice) / Math.abs(lastPrice)) * 100;
};

const getTopReddicksPairs = (pairs, routerAddress, reddicksResourceAddress, limit = 5) =>
  sortPairsByTvl(
    (Array.isArray(pairs) ? pairs : [])
      .map(normalizePairMeta)
      .filter(
        (pair) =>
          pair.address &&
          pair.dexAddress === routerAddress &&
          (pair.baseToken === reddicksResourceAddress ||
            pair.quoteToken === reddicksResourceAddress),
      ),
  ).slice(0, limit);

const getTokenLabelForPair = (pair, reddicksResourceAddress) => {
  if (!pair) {
    return "Unknown / REDDICKS";
  }

  const pairedTokenAddress =
    pair.baseToken === reddicksResourceAddress
      ? pair.quoteToken
      : pair.baseToken;

  if (pairedTokenAddress === GBOF_RESOURCE_ADDRESS) {
    return "GBOF / REDDICKS";
  }

  if (pairedTokenAddress === DCKSLAP_RESOURCE_ADDRESS) {
    return "DCKSLAP / REDDICKS";
  }

  if (typeof pairedTokenAddress === "string" && pairedTokenAddress.length > 18) {
    return `${pairedTokenAddress.slice(0, 10)}... / REDDICKS`;
  }

  return "Token / REDDICKS";
};

export const buildTopPairRows = (
  topPairEntries,
  pairAnalyticsByAddress,
  reddicksResourceAddress,
) =>
  (Array.isArray(topPairEntries) ? topPairEntries : []).map((pairEntry) => {
    const pairAnalytics = pairAnalyticsByAddress?.[pairEntry.address] || null;
    const pairSnapshot = pairAnalytics?.pair || null;
    const latestStat = selectLatestStatEntry(pairAnalytics?.stats);

    return {
      pairAddress: pairEntry.address,
      pairLabel: getTokenLabelForPair(
        pairSnapshot || pairEntry,
        reddicksResourceAddress,
      ),
      tvlUsd: toNumber(pairSnapshot?.tvlUSD ?? pairEntry?.tvlUSD),
      latestVolumeUsd: toFiniteNumberOrNull(latestStat?.volumeUSD),
      latestFeesUsd: toFiniteNumberOrNull(latestStat?.feesUSD),
      spreadPct: buildPairSpreadPct(pairSnapshot),
      updatedAt: pairSnapshot?.updatedAt || pairEntry?.createdAt || null,
    };
  });

export const buildTokenMarketSection = ({
  label,
  symbol = label,
  tokenAddress,
  pairEntry,
  pairAnalytics,
}) => {
  if (!pairEntry && !pairAnalytics) {
    return buildEmptyTokenMarket(label, symbol);
  }

  const pairSnapshot = pairAnalytics?.pair || null;
  const latestStat = selectLatestStatEntry(pairAnalytics?.stats);
  const selectedApy =
    pairSnapshot?.baseToken === tokenAddress
      ? toFiniteNumberOrNull(pairSnapshot?.baseAPY7D)
      : toFiniteNumberOrNull(pairSnapshot?.quoteAPY7D);
  const baseTokenMeta =
    pairAnalytics?.baseToken?.address === tokenAddress ? pairAnalytics.baseToken : null;

  return {
    status: pairSnapshot ? "live" : "pair-only",
    label,
    symbol: baseTokenMeta?.symbol || symbol,
    name: baseTokenMeta?.name || label,
    pairLabel: pairEntry ? `${label} / REDDICKS` : "Official market unavailable",
    pairAddress: pairSnapshot?.address || pairEntry?.address || null,
    pairCreatedAt: pairSnapshot?.createdAt || pairEntry?.createdAt || null,
    priceUnit:
      pairSnapshot?.quoteToken === REDDICKS_RESSOURCE_ADDRESS
        ? "REDDICKS"
        : "paired token",
    currentPrice: toFiniteNumberOrNull(pairSnapshot?.lastPrice),
    bidPrice: toFiniteNumberOrNull(pairSnapshot?.bidPrice),
    askPrice: toFiniteNumberOrNull(pairSnapshot?.askPrice),
    tvlUsd: toNumber(pairSnapshot?.tvlUSD ?? pairEntry?.tvlUSD),
    latestVolumeUsd: toFiniteNumberOrNull(latestStat?.volumeUSD),
    latestFeesUsd: toFiniteNumberOrNull(latestStat?.feesUSD),
    apy7d: selectedApy,
    updatedAt: pairSnapshot?.updatedAt || null,
    latestSnapshotDate: latestStat?.date || null,
    shortage: pairAnalytics?.pairState?.shortage || null,
  };
};

const getCachedSnapshot = ({ allowExpired = false } = {}) => {
  const parsed = parseJson(getStoredValue(REDDICKS_DEX_ANALYTICS_CACHE_KEY), null);
  if (!parsed?.fetchedAt) {
    return null;
  }

  const ageMs = Date.now() - Date.parse(parsed.fetchedAt);
  if (!allowExpired && (!Number.isFinite(ageMs) || ageMs > REDDICKS_DEX_ANALYTICS_TTL_MS)) {
    return null;
  }

  return {
    ...EMPTY_REDDICKS_DEX_ANALYTICS,
    ...parsed,
    overview: {
      ...EMPTY_REDDICKS_DEX_ANALYTICS.overview,
      ...(parsed?.overview || {}),
      officialRouter: {
        ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.officialRouter,
        ...(parsed?.overview?.officialRouter || {}),
      },
      kpis: {
        ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.kpis,
        ...(parsed?.overview?.kpis || {}),
        marketShare: {
          ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.kpis.marketShare,
          ...(parsed?.overview?.kpis?.marketShare || {}),
        },
        flow: {
          ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.kpis.flow,
          ...(parsed?.overview?.kpis?.flow || {}),
          sevenDay: {
            ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.kpis.flow.sevenDay,
            ...(parsed?.overview?.kpis?.flow?.sevenDay || {}),
          },
        },
        deltas: {
          ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.kpis.deltas,
          ...(parsed?.overview?.kpis?.deltas || {}),
          oneDay: {
            ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.kpis.deltas.oneDay,
            ...(parsed?.overview?.kpis?.deltas?.oneDay || {}),
          },
          sevenDay: {
            ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.kpis.deltas.sevenDay,
            ...(parsed?.overview?.kpis?.deltas?.sevenDay || {}),
          },
        },
      },
      latestSnapshot: {
        ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.latestSnapshot,
        ...(parsed?.overview?.latestSnapshot || {}),
      },
      reddicksFamily: {
        ...EMPTY_REDDICKS_DEX_ANALYTICS.overview.reddicksFamily,
        ...(parsed?.overview?.reddicksFamily || {}),
      },
    },
    tokenMarkets: {
      ...EMPTY_REDDICKS_DEX_ANALYTICS.tokenMarkets,
      ...(parsed?.tokenMarkets || {}),
      reddicks: {
        ...EMPTY_REDDICKS_DEX_ANALYTICS.tokenMarkets.reddicks,
        ...(parsed?.tokenMarkets?.reddicks || {}),
      },
      gbof: {
        ...EMPTY_REDDICKS_DEX_ANALYTICS.tokenMarkets.gbof,
        ...(parsed?.tokenMarkets?.gbof || {}),
      },
      dckslap: {
        ...EMPTY_REDDICKS_DEX_ANALYTICS.tokenMarkets.dckslap,
        ...(parsed?.tokenMarkets?.dckslap || {}),
      },
    },
    topPairs: Array.isArray(parsed?.topPairs) ? parsed.topPairs : [],
    isFresh:
      Number.isFinite(ageMs) && ageMs <= REDDICKS_DEX_ANALYTICS_TTL_MS,
  };
};

const writeCachedSnapshot = (snapshot) => {
  setStoredValue(REDDICKS_DEX_ANALYTICS_CACHE_KEY, JSON.stringify(snapshot));
};

const buildUnavailableSnapshot = () => {
  const fetchedAt = new Date().toISOString();

  return {
    ...EMPTY_REDDICKS_DEX_ANALYTICS,
    fetchedAt,
    lastUpdated: null,
    isFresh: true,
  };
};

const buildSnapshotLastUpdated = (latestSnapshot, tokenMarkets, fetchedAt) => {
  const candidateTimestamps = [
    latestSnapshot?.date ? latestSnapshot.date * 1000 : null,
    Date.parse(tokenMarkets?.gbof?.updatedAt || ""),
    Date.parse(tokenMarkets?.dckslap?.updatedAt || ""),
    Date.parse(fetchedAt || ""),
  ].filter((timestamp) => Number.isFinite(timestamp));

  if (candidateTimestamps.length === 0) {
    return null;
  }

  return new Date(Math.max(...candidateTimestamps)).toISOString();
};

const fetchPairAnalytics = async (pairAddress) =>
  fetchJson(`${REDDICKS_DEX_PAIR_ANALYTICS_URL}/${pairAddress}`);

export const getCachedReddicksDexAnalyticsSnapshot = (options = {}) =>
  getCachedSnapshot(options);

export const fetchReddicksDexAnalyticsSnapshot = async () => {
  if (!isMainnetDexSource) {
    return buildUnavailableSnapshot();
  }

  try {
    const [pairsPayload, analyticsPayload] = await Promise.all([
      fetchJsonWithFallback(getReddicksDexPairsCandidateUrls()),
      fetchJson(REDDICKS_DEX_ANALYTICS_URL),
    ]);
    const pairs = getArrayPayload(pairsPayload);
    const trendRows = buildTrendRows(analyticsPayload?.dailies);
    const latestSnapshot =
      trendRows[trendRows.length - 1] ||
      EMPTY_REDDICKS_DEX_ANALYTICS.overview.latestSnapshot;
    const officialRouter = selectOfficialRouterTotal(
      analyticsPayload?.totals,
      REDDICKS_DEX_ROUTER_COMPONENT,
    );
    const reddicksFamily = buildReddicksFamilySummary(
      pairs,
      REDDICKS_DEX_ROUTER_COMPONENT,
      REDDICKS_RESSOURCE_ADDRESS,
    );
    const gbofPair = selectPrimaryTokenPair(
      pairs,
      GBOF_RESOURCE_ADDRESS,
      REDDICKS_DEX_ROUTER_COMPONENT,
    );
    const dckslapPair = selectPrimaryTokenPair(
      pairs,
      DCKSLAP_RESOURCE_ADDRESS,
      REDDICKS_DEX_ROUTER_COMPONENT,
    );
    const topPairEntries = getTopReddicksPairs(
      pairs,
      REDDICKS_DEX_ROUTER_COMPONENT,
      REDDICKS_RESSOURCE_ADDRESS,
      5,
    );
    const pairAnalyticsAddresses = Array.from(
      new Set(
        [gbofPair?.address, dckslapPair?.address]
          .concat(topPairEntries.map((pair) => pair.address))
          .filter((address) => typeof address === "string" && address),
      ),
    );
    const pairAnalyticsResults = await Promise.allSettled(
      pairAnalyticsAddresses.map((pairAddress) => fetchPairAnalytics(pairAddress)),
    );
    const pairAnalyticsByAddress = {};
    pairAnalyticsAddresses.forEach((pairAddress, index) => {
      const result = pairAnalyticsResults[index];
      pairAnalyticsByAddress[pairAddress] =
        result?.status === "fulfilled" ? result.value : null;
    });
    const tokenMarkets = {
      reddicks: reddicksFamily,
      gbof: buildTokenMarketSection({
        label: "GBOF",
        tokenAddress: GBOF_RESOURCE_ADDRESS,
        pairEntry: gbofPair,
        pairAnalytics: gbofPair?.address
          ? pairAnalyticsByAddress[gbofPair.address] || null
          : null,
      }),
      dckslap: buildTokenMarketSection({
        label: "DCKSLAP",
        tokenAddress: DCKSLAP_RESOURCE_ADDRESS,
        pairEntry: dckslapPair,
        pairAnalytics: dckslapPair?.address
          ? pairAnalyticsByAddress[dckslapPair.address] || null
          : null,
      }),
    };
    const topPairs = buildTopPairRows(
      topPairEntries,
      pairAnalyticsByAddress,
      REDDICKS_RESSOURCE_ADDRESS,
    );
    const marketShareKpis = buildRouterShareKpis(
      analyticsPayload?.totals,
      REDDICKS_DEX_ROUTER_COMPONENT,
    );
    const trendFlowKpis = buildTrendFlowKpis(trendRows);
    const trendDeltaKpis = buildTrendDeltaKpis(trendRows);
    const fetchedAt = new Date().toISOString();
    const snapshot = {
      overview: {
        isAvailable: true,
        officialRouter,
        supportingRouters: buildSupportingRouterTotals(
          analyticsPayload?.totals,
          REDDICKS_DEX_ROUTER_COMPONENT,
        ),
        kpis: {
          marketShare: marketShareKpis,
          flow: trendFlowKpis,
          deltas: trendDeltaKpis,
        },
        latestSnapshot,
        reddicksFamily,
      },
      trendRows,
      tokenMarkets,
      topPairs,
      fetchedAt,
      lastUpdated: buildSnapshotLastUpdated(latestSnapshot, tokenMarkets, fetchedAt),
      isFresh: true,
    };

    writeCachedSnapshot(snapshot);
    return snapshot;
  } catch (error) {
    const cachedSnapshot = getCachedSnapshot({ allowExpired: true });
    if (cachedSnapshot) {
      return {
        ...cachedSnapshot,
        isFresh: false,
      };
    }

    throw error;
  }
};
